import React, { useState, useEffect } from 'react';
import { ExternalLink, Github, Calendar, Star, Award } from 'lucide-react';
import { supabase } from '../supabaseClient';

const Portfolio = () => {
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchPortfolioItems();
  }, []);

  const fetchPortfolioItems = async () => {
    try {
      const { data, error } = await supabase
        .from('portfolio_items')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setPortfolioItems(data || []);
    } catch (error) {
      console.error('Error fetching portfolio items:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...new Set(portfolioItems.map(item => item.category))];
  const filteredItems = filter === 'all' 
    ? portfolioItems 
    : portfolioItems.filter(item => item.category === filter);

  const getCategoryColor = (category) => {
    const colors = {
      'web': 'from-blue-400 to-blue-600',
      'mobile': 'from-purple-400 to-purple-600',
      'ai': 'from-green-400 to-green-600',
      'blockchain': 'from-orange-400 to-orange-600',
      'trading': 'from-cyan-400 to-teal-500',
      'saas': 'from-pink-400 to-pink-600',
    };
    return colors[category?.toLowerCase()] || 'from-cyan-400 to-teal-500';
  };

  return (
    <div className="min-h-screen py-16 relative">
      {/* Background decorative elements */}
      <div className="absolute top-40 right-10 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-40 left-10 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl"></div>
      
      <div className="section-container relative z-10">
        {/* Enhanced Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 px-5 py-2.5 glass-card rounded-full text-sm font-semibold text-cyan-400 border border-cyan-500/20 mb-6">
            <Award size={16} className="text-cyan-400" />
            <span>Showcasing Excellence in Development</span>
          </div>
          <h1 className="text-6xl md:text-7xl font-bold font-poppins mb-6">
            Our <span className="gradient-text">Portfolio</span>
          </h1>
          <p className="text-gray-400 text-xl max-w-3xl mx-auto leading-relaxed">
            Explore our collection of successful projects and implementations. Each one crafted with precision, innovation, and attention to detail.
          </p>
        </div>

        {/* Enhanced Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-4 mb-16">
          {categories.map((category) => {
            const isActive = filter === category;
            return (
              <button
                key={category}
                onClick={() => setFilter(category)}
                className={`group relative px-8 py-3.5 rounded-xl font-semibold capitalize transition-all duration-300 ${
                  isActive
                    ? 'bg-gradient-to-r from-cyan-400 to-teal-500 text-gray-900 shadow-lg shadow-cyan-500/30'
                    : 'glass-card text-gray-300 hover:text-white hover:border-cyan-500/30 hover:shadow-lg hover:scale-105'
                }`}
                data-testid={`filter-${category}`}
              >
                <span className="relative z-10">{category}</span>
                {isActive && (
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-cyan-400 to-teal-500 opacity-20 blur-xl"></div>
                )}
              </button>
            );
          })}
        </div>

        {/* Portfolio Stats */}
        {!loading && portfolioItems.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
            <div className="glass-card p-6 text-center rounded-2xl border border-cyan-500/10">
              <div className="text-4xl font-bold gradient-text mb-2">{portfolioItems.length}</div>
              <div className="text-gray-400 text-sm">Total Projects</div>
            </div>
            <div className="glass-card p-6 text-center rounded-2xl border border-cyan-500/10">
              <div className="text-4xl font-bold gradient-text mb-2">{categories.length - 1}</div>
              <div className="text-gray-400 text-sm">Categories</div>
            </div>
            <div className="glass-card p-6 text-center rounded-2xl border border-cyan-500/10">
              <div className="text-4xl font-bold gradient-text mb-2">100%</div>
              <div className="text-gray-400 text-sm">Success Rate</div>
            </div>
            <div className="glass-card p-6 text-center rounded-2xl border border-cyan-500/10">
              <div className="text-4xl font-bold gradient-text mb-2">24/7</div>
              <div className="text-gray-400 text-sm">Support</div>
            </div>
          </div>
        )}

        {/* Portfolio Grid */}
        {loading ? (
          <div className="text-center text-gray-400 py-32">
            <div className="relative w-20 h-20 mx-auto mb-8">
              <div className="absolute inset-0 border-4 border-cyan-500/20 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p className="text-xl font-semibold">Loading portfolio...</p>
            <p className="text-sm text-gray-500 mt-2">Preparing our best work for you</p>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center text-gray-400 py-32">
            <div className="glass-card max-w-md mx-auto p-12 rounded-3xl">
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-cyan-400/20 to-teal-500/20 rounded-full flex items-center justify-center">
                <Star size={40} className="text-cyan-400" />
              </div>
              <p className="text-3xl font-bold mb-4">No projects yet</p>
              <p className="text-base text-gray-500">Check back soon for our latest projects</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredItems.map((item, index) => (
              <div 
                key={item.id} 
                className="glass-card overflow-hidden rounded-2xl card-hover group relative"
                data-testid={`portfolio-item-${item.id}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Image with Overlay */}
                <div className="relative h-72 overflow-hidden bg-gradient-to-br from-gray-900 to-gray-800">
                  <img 
                    src={item.image_url || 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop'} 
                    alt={item.title}
                    className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 group-hover:rotate-1"
                  />
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-500" />
                  
                  {/* Category Badge */}
                  <div className="absolute top-4 right-4">
                    <span className={`px-4 py-2 bg-gradient-to-r ${getCategoryColor(item.category)} text-white text-xs font-bold rounded-full uppercase shadow-lg`}>
                      {item.category}
                    </span>
                  </div>

                  {/* Hover Actions */}
                  <div className="absolute top-4 left-4 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {item.github_url && (
                      <a 
                        href={item.github_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2.5 bg-gray-900/80 backdrop-blur-sm hover:bg-cyan-500 text-white rounded-lg transition-colors"
                        aria-label="GitHub"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Github size={18} />
                      </a>
                    )}
                    {item.live_url && (
                      <a 
                        href={item.live_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2.5 bg-gray-900/80 backdrop-blur-sm hover:bg-cyan-500 text-white rounded-lg transition-colors"
                        aria-label="Live Demo"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink size={18} />
                      </a>
                    )}
                  </div>

                  {/* Bottom Title Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                    <h3 className="text-2xl font-bold text-white mb-2 drop-shadow-lg">
                      {item.title}
                    </h3>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <p className="text-gray-400 mb-4 leading-relaxed line-clamp-2">
                    {item.description}
                  </p>

                  {/* Tech Stack */}
                  {item.tech_stack && item.tech_stack.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-5">
                      {item.tech_stack.slice(0, 4).map((tech, index) => (
                        <span 
                          key={index}
                          className="px-3 py-1.5 bg-gradient-to-r from-cyan-500/10 to-teal-500/10 text-cyan-400 text-xs font-semibold rounded-lg border border-cyan-500/20"
                        >
                          {tech}
                        </span>
                      ))}
                      {item.tech_stack.length > 4 && (
                        <span className="px-3 py-1.5 bg-gray-800/50 text-gray-400 text-xs rounded-lg">
                          +{item.tech_stack.length - 4}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-800/50">
                    <div className="flex items-center space-x-2 text-gray-500 text-sm">
                      <Calendar size={16} />
                      <span>{new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>
                    </div>
                    <div className="flex items-center space-x-1 text-yellow-400">
                      <Star size={16} fill="currentColor" />
                      <span className="text-sm font-semibold">Featured</span>
                    </div>
                  </div>
                </div>

                {/* Hover Border Effect */}
                <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-cyan-500/30 transition-colors duration-300 pointer-events-none"></div>
              </div>
            ))}
          </div>
        )}

        {/* CTA Section */}
        {!loading && filteredItems.length > 0 && (
          <div className="mt-20 glass-card p-12 text-center rounded-3xl border-2 border-cyan-500/20">
            <h2 className="text-4xl font-bold font-poppins mb-4">
              Like What You <span className="gradient-text">See</span>?
            </h2>
            <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
              Let's build something amazing together. Get in touch to discuss your project.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/shop" className="gradient-button inline-flex items-center justify-center space-x-2 text-lg px-10 py-5 rounded-xl">
                <span>Browse Products</span>
              </a>
              <a href="/contact" className="glass-card inline-flex items-center justify-center px-10 py-5 rounded-xl text-gray-300 hover:text-white hover:border-cyan-500/30 transition-all">
                <span>Start a Project</span>
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Portfolio;